import { MockGrain, MockLocationRow } from './LocationSummaryV2MockData';

export type HierarchyMode = 'region' | 'market' | 'flat';

export type SearchContext = {
  hasQuery: boolean;
  matchingSourceIds: Set<string>;
  matchingMarketKeys: Set<string>;
  matchingRegionKeys: Set<string>;
};

/**
 * Determine which hierarchy mode should be active based on selected grains.
 */
export const deriveActiveHierarchyMode = (grains: MockGrain[]): HierarchyMode => {
  const hasTimeGrain = grains.some(g => ['year', 'period', 'week'].includes(g));
  const locationGrains = grains.filter(g => ['region', 'market', 'location'].includes(g));

  if (hasTimeGrain || locationGrains.length !== grains.length) {
    return 'flat';
  }

  if (grains.length === 1 && grains[0] === 'region') {
    return 'region';
  }

  if (
    grains.length === 2 &&
    grains.includes('region') &&
    grains.includes('market')
  ) {
    return 'market';
  }

  return 'flat';
};

/**
 * Find which source rows match the search query.
 */
export const determineSearchMatches = (
  sourceRows: MockLocationRow[],
  query: string
): Set<string> => {
  if (!query.trim()) return new Set();

  const lowerQuery = query.toLowerCase();
  const matches = new Set<string>();

  sourceRows.forEach(row => {
    const searchableText = [row.region, row.market, row.location]
      .filter(Boolean)
      .join(' ')
      .toLowerCase();

    if (searchableText.includes(lowerQuery)) {
      matches.add(row.id);
    }
  });

  return matches;
};

/**
 * Build explicit search context from query and hierarchy.
 */
export const buildSearchContext = (
  hierarchyRows: MockLocationRow[],
  sourceRows: MockLocationRow[],
  query: string,
  mode: HierarchyMode
): SearchContext => {
  const normalizedQuery = query.trim();
  
  const result: SearchContext = {
    hasQuery: normalizedQuery.length > 0,
    matchingSourceIds: new Set(),
    matchingMarketKeys: new Set(),
    matchingRegionKeys: new Set(),
  };

  if (!normalizedQuery) return result;

  const searchMatches = determineSearchMatches(sourceRows, normalizedQuery);
  result.matchingSourceIds = searchMatches;

  if (mode === 'region') {
    hierarchyRows.forEach(row => {
      if (row.groupType === 'location' && searchMatches.has(row.id)) {
        if (row.parentMarketExpandKey) {
          result.matchingMarketKeys.add(row.parentMarketExpandKey);
        }
        if (row.parentRegionExpandKey) {
          result.matchingRegionKeys.add(row.parentRegionExpandKey);
        }
      }
      if (row.groupType === 'market' && searchMatches.has(row.id)) {
        result.matchingMarketKeys.add(row.id);
        if (row.parentRegionExpandKey) {
          result.matchingRegionKeys.add(row.parentRegionExpandKey);
        }
      }
      if (row.groupType === 'region' && searchMatches.has(row.id)) {
        result.matchingRegionKeys.add(row.id);
      }
    });
  } else if (mode === 'market') {
    hierarchyRows.forEach(row => {
      if (row.groupType === 'location' && searchMatches.has(row.id)) {
        if (row.parentMarketExpandKey) {
          result.matchingMarketKeys.add(row.parentMarketExpandKey);
        }
      }
      if (row.groupType === 'market' && searchMatches.has(row.id)) {
        result.matchingMarketKeys.add(row.id);
      }
    });
  }

  return result;
};

/**
 * Determine which hierarchy nodes auto-expand based on search context.
 */
export const determineAutoExpandKeys = (
  hierarchyRows: MockLocationRow[],
  searchContext: SearchContext,
  mode: HierarchyMode
): Set<string> => {
  const toExpand = new Set<string>();

  if (searchContext.matchingSourceIds.size === 0) return toExpand;

  if (mode === 'region') {
    searchContext.matchingMarketKeys.forEach(key => toExpand.add(key));
    searchContext.matchingRegionKeys.forEach(key => toExpand.add(key));
  } else if (mode === 'market') {
    searchContext.matchingMarketKeys.forEach(key => toExpand.add(key));
  }

  return toExpand;
};

/**
 * Filter hierarchy rows for visibility based on expansion state and search context.
 */
export const filterForVisibility = (
  hierarchyRows: MockLocationRow[],
  mode: HierarchyMode,
  expandedGroups: Set<string>,
  searchContext: SearchContext
): MockLocationRow[] => {
  const hasActiveSearch = searchContext.hasQuery;

  if (mode === 'flat') {
    if (!hasActiveSearch) return hierarchyRows;
    return hierarchyRows.filter(row => searchContext.matchingSourceIds.has(row.id));
  }

  if (mode === 'region') {
    return hierarchyRows.filter(row => {
      if (row.groupType === 'region') {
        if (!hasActiveSearch) return true;
        return searchContext.matchingRegionKeys.has(row.id);
      }

      if (row.groupType === 'market') {
        if (!hasActiveSearch) {
          return expandedGroups.has(row.parentRegionExpandKey ?? '');
        }

        const isSearchRelevant = searchContext.matchingMarketKeys.has(row.id);
        const parentExpanded = expandedGroups.has(row.parentRegionExpandKey ?? '');
        return isSearchRelevant && parentExpanded;
      }

      if (row.groupType === 'location') {
        if (!hasActiveSearch) {
          const marketExpanded = expandedGroups.has(row.parentMarketExpandKey ?? '');
          const regionExpanded = expandedGroups.has(row.parentRegionExpandKey ?? '');
          return marketExpanded && regionExpanded;
        }

        const matches = searchContext.matchingSourceIds.has(row.id);
        const marketExpanded = expandedGroups.has(row.parentMarketExpandKey ?? '');
        const regionExpanded = expandedGroups.has(row.parentRegionExpandKey ?? '');

        return matches && marketExpanded && regionExpanded;
      }

      return false;
    });
  }

  if (mode === 'market') {
    return hierarchyRows.filter(row => {
      if (row.groupType === 'market') {
        if (!hasActiveSearch) return true;
        return searchContext.matchingMarketKeys.has(row.id);
      }

      if (row.groupType === 'location') {
        if (!hasActiveSearch) {
          return expandedGroups.has(row.parentMarketExpandKey ?? '');
        }

        const locationMatches = searchContext.matchingSourceIds.has(row.id);
        const marketMatches = searchContext.matchingMarketKeys.has(row.parentMarketExpandKey ?? '');
        const marketExpanded = expandedGroups.has(row.parentMarketExpandKey ?? '');

        if (locationMatches) return true;
        return marketMatches && marketExpanded;
      }

      return false;
    });
  }

  return hierarchyRows;
};
